// ==============================================
// InlineRename — a name with a visible Rename button that turns into an
// input. Enter or Save saves, Escape or Cancel backs out. Used for device
// (boat) names and, in the admin console, user names.
// ==============================================

import { useState, type ReactNode } from 'react';
import { PencilSquareIcon } from '@heroicons/react/24/outline';
import clsx from 'clsx';

interface InlineRenameProps {
  value: string;
  onSave: (next: string) => Promise<unknown>;
  /** Accessible name for the button and the input, e.g. "Rename device". */
  label: string;
  /** One line under the input saying what else the rename changes. */
  hint?: string;
  /** How the name renders when not editing (the element around the text). */
  display?: (value: string) => ReactNode;
  maxLength?: number;
  className?: string;
}

export function InlineRename({ value, onSave, label, hint, display, maxLength = 120, className }: InlineRenameProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(value);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const open = () => { setDraft(value); setError(null); setEditing(true); };
  const cancel = () => { setEditing(false); setError(null); };
  const save = async () => {
    const next = draft.trim();
    if (!next || next === value) { setEditing(false); return; }
    setSaving(true);
    setError(null);
    try {
      await onSave(next);
      setEditing(false);
    } catch {
      setError('Could not save. Try again.');
    } finally {
      setSaving(false);
    }
  };

  if (!editing) {
    return (
      <span className={clsx('inline-flex items-center gap-2', className)}>
        {display ? display(value) : <span>{value}</span>}
        <button
          type="button"
          onClick={open}
          aria-label={label}
          title={hint ? `${label} — ${hint}` : label}
          className="p-1 rounded text-slate-400 hover:text-cyan-400 hover:bg-slate-700 transition-colors"
        >
          <PencilSquareIcon className="w-5 h-5" />
        </button>
      </span>
    );
  }

  return (
    <span className={clsx('inline-flex flex-col gap-1', className)}>
      <span className="inline-flex items-center gap-2">
        <input
          type="text"
          value={draft}
          aria-label={label}
          maxLength={maxLength}
          autoFocus
          disabled={saving}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') { e.preventDefault(); void save(); }
            if (e.key === 'Escape') { e.preventDefault(); cancel(); }
          }}
          className="px-2 py-1 bg-slate-700 border border-slate-600 rounded text-white text-base focus:outline-none focus:border-cyan-500"
        />
        <button
          type="button"
          onClick={() => void save()}
          disabled={saving}
          className="px-3 py-1 rounded bg-cyan-600 hover:bg-cyan-500 text-white text-sm font-medium disabled:opacity-50"
        >
          Save
        </button>
        <button type="button" onClick={cancel} disabled={saving} className="px-2 py-1 rounded text-slate-300 hover:text-white text-sm">
          Cancel
        </button>
      </span>
      {hint && <span className="text-sm text-slate-400">{hint}</span>}
      {error && <span role="alert" className="text-sm text-red-400">{error}</span>}
    </span>
  );
}

export default InlineRename;
